<template>
  <box>
    <data-table
      :data="users"
      :columns="columns"
      :searchable="true"
      :search-placeholder="$t('users.searchPlaceholder')"
      :striped="true"
      :hoverable="true"
      :show-pagination="true"
      :items-per-page-options="[10, 25, 50, 100]"
      :default-items-per-page="perPage"
      default-sort-by="created_at"
      default-sort-order="desc"
      :loading="loading"
      :empty-message="$t('datatable.noData')"
      :server-side="true"
      :total-items="totalItems"
      @search="handleSearch"
      @sort="handleSort"
      @page-change="handlePageChange"
      @page-size-change="handlePageSizeChange"
    >
      <!-- Custom cell for STT (Serial Number) -->
      <template #cell-stt="{ index }">
        {{ index + 1 }}
      </template>

      <!-- Custom cell for Permissions -->
      <template #cell-permissions="{ value }">
        <div class="tags">
          <span v-for="scope in value" :key="scope" class="tag is-primary is-small">
            {{ scope }}
          </span>
        </div>
      </template>

      <!-- Custom cell for Status -->
      <template #cell-status="{ value }">
        <span v-if="value === 'active'" class="tag is-success is-light">
          {{ $t('users.statusActive') }}
        </span>
        <span v-else class="tag is-warning is-light">
          {{ $t('users.statusInactive') }}
        </span>
      </template>

      <!-- Custom cell for Created Time -->
      <template #cell-created_at="{ value }">
        <span v-if="value">{{ value | dmyhs }}</span>
        <span v-else>-</span>
      </template>

      <!-- Custom cell for Actions -->
      <template #cell-actions="{ row }">
        <user-actions
          :user="row"
          :current-user-id="currentUserId"
          @edit="handleEdit"
          @reset-password="handleResetPassword"
          @toggle-status="handleToggleStatus"
          @force-logout="handleForceLogout"
        />
      </template>
    </data-table>
  </box>
</template>

<script>
import DataTable from '@/components/common/DataTable.vue'
import UserActions from './UserActions.vue'

export default {
  name: 'UserList',
  components: {
    DataTable,
    UserActions
  },
  props: {
    searchFilter: {
      type: String,
      default: ''
    },
    theme: {
      type: Object,
      required: true
    },
    filters: {
      type: Object,
      default: () => ({
        scopes: [],
        statuses: []
      })
    },
    perPage: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      users: [],
      loading: false,
      totalItems: 0,
      currentPage: 1,
      currentPageSize: this.perPage || 10,
      currentSort: {
        by: 'created_at',
        order: 'desc'
      },
      currentSearch: ''
    }
  },
  computed: {
    currentUserId() {
      const user = this.$store.getters['authentication/user']
      return user ? user.id() : null
    },
    columns() {
      return [
        {
          key: 'stt',
          label: this.$t('users.serialNumber'),
          sortable: true,
          width: '80px',
          align: 'center'
        },
        {
          key: 'name',
          label: this.$t('users.username'),
          field: 'name',
          sortable: true,
          width: '150px',
          truncate: true
        },
        {
          key: 'email',
          label: 'Email',
          field: 'email',
          sortable: true,
          width: '200px',
          truncate: true
        },
        {
          key: 'permissions',
          label: this.$t('users.role'),
          field: 'scopes',
          sortable: true,
          width: '150px'
        },
        {
          key: 'status',
          label: this.$t('users.status'),
          field: 'status',
          sortable: true,
          width: '120px',
          align: 'center'
        },
        {
          key: 'created_at',
          label: this.$t('users.createdTime'),
          field: 'created_at',
          sortable: true,
          width: '180px'
        },
        {
          key: 'actions',
          label: this.$t('users.actions'),
          sortable: false,
          width: '150px',
          align: 'center'
        }
      ]
    }
  },
  watch: {
    /**
     * Watch for filter changes and reload data
     */
    filters: {
      handler() {
        this.fetchUsers()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchUsers()
  },
  methods: {
    /**
     * Fetches users data from API with pagination, sorting, and search
     */
    async fetchUsers() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          limit: this.currentPageSize,
          sort: this.currentSort.by,
          order: this.currentSort.order === 'asc' ? 'asc' : 'desc'
        }

        // Add search filter if present
        if (this.currentSearch) {
          params.filter = this.currentSearch
        }

        // Add scope filters if any are selected
        if (this.filters.scopes && this.filters.scopes.length > 0) {
          params.scopes = this.filters.scopes.join(',')
        }

        // Add status filters if any are selected
        if (this.filters.statuses && this.filters.statuses.length > 0) {
          params.statuses = this.filters.statuses.join(',')
        }

        const response = await this.$http.get('/api/v1/users', { params })

        // Only update data after successful fetch to prevent flicker
        if (response && response.data && response.data.data) {
          this.users = response.data.data.items || []
          this.totalItems = response.data.data.total || 0
        }
      } catch (error) {
        console.error('Error fetching users:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || 'Failed to fetch users'
        })
        // Don't clear users on error to prevent flicker
      } finally {
        this.loading = false
      }
    },
    /**
     * Handle search event from DataTable
     */
    handleSearch(searchQuery) {
      this.currentSearch = searchQuery
      this.currentPage = 1 // Reset to first page on search
      this.fetchUsers()
    },
    /**
     * Handle sort event from DataTable
     */
    handleSort(sortConfig) {
      this.currentSort = {
        by: sortConfig.by || 'created_at',
        order: sortConfig.order || 'desc'
      }
      this.currentPage = 1 // Reset to first page on sort
      this.fetchUsers()
    },
    /**
     * Handle page change event from DataTable
     */
    handlePageChange(page) {
      this.currentPage = page
      this.fetchUsers()
    },
    /**
     * Handle page size change event from DataTable
     */
    handlePageSizeChange(pageSize) {
      this.currentPageSize = pageSize
      this.currentPage = 1 // Reset to first page on page size change
      this.fetchUsers()
    },
    /**
     * Handles user edit event
     */
    handleEdit(user) {
      this.$emit('user-edit', user)
    },
    /**
     * Handles password reset event
     */
    handleResetPassword(user) {
      this.$emit('user-reset-password', user)
    },
    /**
     * Handles user status toggle event
     */
    handleToggleStatus(user) {
      this.$emit('user-toggle-status', user)
    },
    /**
     * Handles force logout event
     */
    handleForceLogout(user) {
      this.$emit('user-force-logout', user)
    },
    /**
     * Reloads the table data
     */
    reloadTable() {
      this.fetchUsers()
    }
  }
}
</script>
